import { enableProdMode, InjectionToken } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import type { IPPioWindow } from '@partnerportal.io/applibrary';
import type { ByNameResponse } from '@partnerportal.io/apitypes';
import { NO_COMPANY_RESPONSE } from '@partnerportal.io/applibrary/constants';
import { environment } from '@environment';
import { CookieService } from 'ngx-cookie-service';

export const PATH_SIGNIN: string = '/sign-in';


/**
 * We set the environment on the window this way it's available throughout the application, even in other modules where it's required prior to angular booting up
 * This is particularly relevant to {@link @partnerportal.io/applibrary}'s Auth0Module
 */
(window as IPPioWindow).config = environment;
(window as IPPioWindow).config.logoutReturnTo = generateLogoutReturnTo();

// !!! DO NOT IMPORT THIS DIRECTLY - SEE NOTE BELOW IN init()
// import { AppModule } from './app/app.module';


/**
 * This is _only_ used by {@link CompanyInfo}.
 *
 * Everyone else should get data from `CompanyInfo` not from this InjectionToken.
 */
export const HTTP_RESPONSE_BY_NAME: InjectionToken<ByNameResponse>
  = new InjectionToken<ByNameResponse>('Initial Response from the Companies/by-name endpoint');

/**
 * Gets the ICompanyPublicInfo by calling the /by-name endpoint and stores it in `window.company` so it is
 * available for static usage in Module initializers - mainly for the AuthModule (CompanyInfo should be
 * used everywhere else).
 */
function getByName(): Promise<ByNameResponse> {
	const { apiUrl } = environment;
	const subdomain = getSubdomain();
	return fetch(`${apiUrl}/Companies/by-name?subdomain=${subdomain}`)
	.then<ByNameResponse>(response => {
		if (response.status >= 400) {
			return NO_COMPANY_RESPONSE;
		}
		return response?.json();
	})
	.catch<ByNameResponse>(err => {
		console.error(err);
		return NO_COMPANY_RESPONSE;
	})
	.then(response => {
		response ||= NO_COMPANY_RESPONSE;
		// console.debug('getByName', { subdomain, response });
		(window as IPPioWindow).company = response;
		return response;
	});
}

function getSubdomain() {
	const { rootDomain } = environment;
	const host = window.location.host;
	const cookieService: CookieService = new CookieService(document, 'browser' as any);

	// Match the regex to get the subdomain. Use 'x-forwarded-host' if sitting behind a proxy.
	const matches = new RegExp(`(.+?)\\.${rootDomain}`, 'g').exec(host);
	let subdomain = matches && matches[1] || host.split(':')[0];

	if (environment.debuggable && cookieService.get('ppio_force_subdomain')) {
		subdomain = cookieService.get('ppio_force_subdomain');
	}

	return subdomain;
}

/**
 * Because the web app will be running on a subdomain or a custom domain, or running locally, we need to direct the user back either through the auth0 integration service or directly back here.
 * This is used to determine that logic as the Auth0 Auth service simply uses the environment variable (or an optional override) to determine where to route the user after logout.
 *
 * @return {*}  {string}
 */
function generateLogoutReturnTo(): string {
	const isNonProductionOrigin: boolean = origin?.includes('//localhost:') || origin?.includes('.partnerportal.dev');
	const returnToOrigin: string = isNonProductionOrigin ? origin : environment.apiUrl;
	const returnToPath: string = isNonProductionOrigin ? PATH_SIGNIN : '/1.0/auth0/logout';

	const returnToURL: URL = new URL(`${returnToOrigin}${returnToPath}`);

	return returnToURL.toString();
}

function init(byNameResponse: ByNameResponse) {

	if (environment.debuggable) {
		console.debug('by-name', { byNameResponse });
	}

	if (environment.production) {
		enableProdMode();
	}

	/**
	 * This 'hack' is required because we need to prevent the static content in AuthModule from loading
	 * until this function runs. Without this hack, when the AppModule is referenced in the imports for
	 * this file, the Javascript loader loads AppModule, which references AuthModule and therefore
	 * AuthModule is loaded. If AuthModule static content is loaded before this init function, then the
	 * `by-name` data is not available to it and the Organization Id is not determined, which breaks auth.
	 */
	import(/* webpackChunkName: "lazyload-app" */'./app/app.module').then(response => {

		platformBrowserDynamic(
			[
				{
					provide: HTTP_RESPONSE_BY_NAME,
					useValue: byNameResponse,
					multi: true,
				},
			],
		)
		.bootstrapModule(response.AppModule, { preserveWhitespaces: true })
		// .bootstrapModule(AppModule, { preserveWhitespaces: true })
		.catch(err => console.error(err));
	});

}

getByName().then(init);
